import * as React from 'react';
import { Formik, FieldArray, FormikProps } from 'formik';
import ParameterField, { buildParametersValidationSchema } from './ParameterField';
import { CaseRunCalculateModel, CaseRunCreateModelBase, RunParameterModel } from '@app/api2/api';
import { Table, Icon, FormField as SField, FormField, Label } from 'semantic-ui-react';
import { isParameterDisabled, isParameterVisible } from './Parameters/Utils';
import { IParameterRenderer } from "./ParameterRendererAbstracts";
import { ChangeObserver } from '../Labs/ChangeObserver';
import { isNullEmptyObject } from '../Common/Utils';
import { useTranslation2 } from '../Contexts/TranslationContext2';


export interface ISimpleParametersProps {
    labId: number;
    parameters: RunParameterModel[];
    onParametersChanged?: (parameters: RunParameterModel[]) => void;
    onValidationChanged?: (isValid: boolean) => void;
    // onChanged?: (values: { [key: string]: any }) => void;
    parameterRenderer?: IParameterRenderer; // optional renderer (keeps parity with existing interfaces)
    disabled?: boolean;
    secondaryParametersCollapsed?: boolean;
    caseRunCreateModel: CaseRunCreateModelBase | CaseRunCalculateModel;
}

export const SimpleParameters: React.FC<ISimpleParametersProps> = ({ labId, parameters, onParametersChanged, onValidationChanged, parameterRenderer, disabled, secondaryParametersCollapsed, caseRunCreateModel }) => {
    const { translations: t } = useTranslation2();

    const initialValues: { [k: string]: any } = {};
    for (const p of parameters || []) {
        initialValues[p.parameter] = p.value;
    }

    const [showSecondary, setShowSecondary] = React.useState(false);

    const validationSchema = buildParametersValidationSchema(parameters || []);

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={() => { }}
            enableReinitialize={false} validateOnMount validateOnChange>
            {(formik: FormikProps<any>) => {
                // <ChangeObserver value={formik.values} onChange={(vals) => {
                //     console.log('Parameters changed:', vals);
                //     if (onChanged) onChanged(vals);
                // }} />;


                const visiblePrimary = (parameters || [])
                    .filter(x => !secondaryParametersCollapsed || !x.isSecondary)
                    .filter(p => {
                        try { return isParameterVisible(p, formik.values); } catch { return true; }
                    });

                const secondaryParams = (parameters || []).filter(x => x.isSecondary);

                const renderParamRow = (p: RunParameterModel) => (
                    <Table.Row key={p.parameter}>
                        <Table.Cell width={5}>{t.parameter.names[p.parameter] || p.name || p.parameter}</Table.Cell>
                        <Table.Cell>
                            {parameterRenderer && parameterRenderer.canRenderParameter && parameterRenderer.canRenderParameter(p.parameter) ? (
                                // console.log('Using custom parameter renderer for', parameterRenderer),
                                // wrap custom renderer in Form.Field so Semantic UI shows validation error styling
                                <SField error={!!(formik.errors && formik.errors[p.parameter])}>
                                    {parameterRenderer.renderParameterEditor?.(caseRunCreateModel, p, formik.values, async (param, value) => {
                                        // console.log('Custom parameter changed:', param, value);
                                        const errors = await formik.setFieldValue(param.parameter || param.parameter, value);
                                        // console.log('Set field value result:', errors);
                                        // if (isNullEmptyObject(errors)) {
                                        onParametersChanged && onParametersChanged([...parameters.map(pp => pp.parameter === param.parameter ? { ...param, value: value } : pp)]);
                                        // }
                                    }, formik.errors, formik.setFieldValue, null)}
                                    {!!(formik.errors && formik.errors[p.parameter]) && <Label basic color="red" pointing>{String(formik.errors[p.parameter])}</Label>}
                                </SField>
                            ) : (
                                <ParameterField parameter={p} disabled={disabled || isParameterDisabled(p)} name={p.parameter}
                                    onValueChange={(pp, v) => {
                                        onParametersChanged && onParametersChanged([...parameters.map(param => param.parameter === pp.parameter ? { ...param, value: v } : param)]);
                                    }} />
                            )}
                        </Table.Cell>
                    </Table.Row>
                );

                return (
                    <>
                        <ChangeObserver value={formik.isValid} onChange={(isValid) => {
                            if (onValidationChanged) onValidationChanged(isValid);
                        }} />
                        <Table definition striped>
                            <Table.Body>
                                {visiblePrimary.map(p => renderParamRow(p))}
                            </Table.Body>

                            {secondaryParametersCollapsed && secondaryParams.length > 0 && (
                                <Table.Body className="secondary">
                                    <Table.Row>
                                        <Table.Cell colSpan={2}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setShowSecondary(s => !s); }}>
                                                <span>Advanced options</span>
                                                <Icon name={showSecondary ? 'caret down' : 'caret right'} />
                                            </a>
                                        </Table.Cell>
                                    </Table.Row>
                                    {showSecondary && secondaryParams.filter(p => {
                                        try { return isParameterVisible(p, formik.values); } catch { return true; }
                                    }).map(p => renderParamRow(p))}
                                </Table.Body>
                            )}
                        </Table>
                    </>

                );
            }}
        </Formik>
    );
}

export default SimpleParameters;
